import { Injectable } from '@angular/core';
import { ConfigService } from '../config.service';
import { HttpClient } from '@angular/common/http';
import { DataTransform, DateField } from './dataTransformFunctions';
import { Observable, of, switchMap } from 'rxjs';
import { AssignedPartnershipInfo, Customer, Trip, TripDetail, customerDateFields, tripDateFields } from '.';
import { TripPartnershipService } from './trip-partnership.service';

export interface Journals {
  id: number,
  tripId: number,
  entry: string,
  entrydate: Date,
  timestamp: string,
  iscomment: number,
  photo: number,
  entrytitle?: string,
  userId: number,
  approved: number,
  partner: number,
  videoId: string | null,
  videoType: VideoTypes | null,
  author: string
}

export interface JournalHomeTrip {
  id: number,
  groupingId: string | null,
  tripDate: Date,
  tripEndDate: Date,
  custId?: number,
  locId?: string,
  customerName: string,
  tlId: number,
  tlConfirmed: number,
  atl1Confirmed: number,
  atl2Confirmed: number,
  atl3Confirmed: number,
  atl4Confirmed: number,
  atl5Confirmed: number,
  atl6Confirmed: number,
  atl7Confirmed: number,
  atl8Confirmed: number,
  atl9Confirmed: number,
  atl10Confirmed: number,
  atl11Confirmed: number,
  atl12Confirmed: number,
  atl13Confirmed: number,
  atl14Confirmed: number,
  atl15Confirmed: number,
  atl16Confirmed: number,
  atl1: number,
  atl2: number,
  atl3: number,
  atl4: number,
  atl5: number,
  atl6: number,
  atl7: number,
  atl8: number,
  atl9: number,
  atl10: number,
  atl11: number,
  atl12: number,
  atl13: number,
  atl14: number,
  atl15: number,
  atl16: number,

}

export interface IndividualTripJournal {
  trip: TripDetail,
  customer: Customer,
  partnership: AssignedPartnershipInfo,
  entries: Journals[]
}

export interface AddJournalEntryData {
  content: string,
  author: string,
  title: string,
  videoType?: VideoTypes | null
  videoId?: string | null
}

export type EditJournalEntryData = Partial<Pick<AddJournalEntryData, "author" | "content" | "title" | "videoId" | "videoType">>


export enum VideoTypes {
  YouTube = "youtube"
}

@Injectable({
  providedIn: 'root'
})
export class TripJournalService {

  private readonly journalHomeDateFields: DateField<JournalHomeTrip>[] = [
    { dateType: "local", fieldName: "tripDate" },
    { dateType: "local", fieldName: "tripEndDate" }
  ]

  private readonly journalDateFields: DateField<Journals>[] = [
    {dateType: "central", fieldName: "entrydate"}
  ]

  constructor(
    private config: ConfigService,
    private http: HttpClient,
    private partnerService: TripPartnershipService
  ) { }


  getTripJournalHomeTrips() {
    return this.http.get<JournalHomeTrip[]>(`${this.config.dataApiUrl}/trip-journals/home-trips`).pipe(
      switchMap(results => DataTransform.replaceDatesArray(results, this.journalHomeDateFields))
    )
  }

  getIndividualJournal(tripId: number): Observable<IndividualTripJournal> {
    return this.http.get<IndividualTripJournal>(`${this.config.dataApiUrl}/trip-journals/trip/${tripId}`).pipe(
      switchMap(results => {
      
        return of({
          trip: DataTransform.replaceDatesDirect<TripDetail>(results.trip, tripDateFields),
          customer: DataTransform.replaceDatesDirect(results.customer, customerDateFields),
          partnership: this.partnerService.replacePartnerAssignmentDatesDirect(results.partnership),
          entries: DataTransform.replaceDatesArrayDirect<Journals>(results.entries, this.journalDateFields)
        })
      })
    )
  }

  addStaffJournalEntry(entryData: AddJournalEntryData, tripId: number) {
    return this.http.post<Journals>(`${this.config.dataApiUrl}/trip-journals/trip/${tripId}`, entryData).pipe(
      switchMap(results => DataTransform.replaceDates<Journals>(results, this.journalDateFields))
    )
  }

  addJournalEntryPhoto(tripId: number, entryId: number, image: File) {
    const formData = new FormData()

    formData.append("files", image)
    return this.http.post(`${this.config.dataApiUrl}/trip-journals/trip/${tripId}/entry/${entryId}/photo`, formData)
  }


  editStaffJournalEntry(entryId: number, entryData: EditJournalEntryData ) {
    return this.http.patch<Journals>(`${this.config.dataApiUrl}/trip-journals/entry/${entryId}`, entryData).pipe(
      switchMap(results => DataTransform.replaceDates<Journals>(results, this.journalDateFields))
    )
  }

  removeJournalEntryPhoto(entryId: number) {
    return this.http.delete<void>(`${this.config.dataApiUrl}/trip-journals/entry/${entryId}/photo`)
  }

  removeGroupPhoto(tripId: number) {
    return this.http.delete<void>(`${this.config.dataApiUrl}/trip-journals/trip/${tripId.toString()}/group-photo`)
  }

  deleteJournalEntry(entryId: number) {
    return this.http.delete<void>(`${this.config.dataApiUrl}/trip-journals/entry/${entryId}`)
  }

  notifyJournalEntry(entryId: number) {
    return this.http.post<void>(`${this.config.dataApiUrl}/trip-journals/entry/${entryId}/notify`, {})
  }

}
